import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// Thunks
export const fetchEspecialidades = createAsyncThunk(
  'especialidades/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/especialidades')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al obtener especialidades')
    }
  }
)

export const fetchEspecialidadById = createAsyncThunk(
  'especialidades/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/especialidades/${id}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al obtener especialidad')
    }
  }
)

export const fetchEspecialidadesStats = createAsyncThunk(
  'especialidades/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/especialidades/stats')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al obtener estadísticas')
    }
  }
)

export const createEspecialidad = createAsyncThunk(
  'especialidades/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/especialidades', data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || error.response?.data?.errores || 'Error al crear especialidad')
    }
  }
)

export const updateEspecialidad = createAsyncThunk(
  'especialidades/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/especialidades/${id}`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || error.response?.data?.errores || 'Error al actualizar especialidad')
    }
  }
)

export const deleteEspecialidad = createAsyncThunk(
  'especialidades/delete',
  async ({ id, confirmacion }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/especialidades/${id}`, {
        data: { confirmacion }
      })
      return { id, mensaje: response.data.mensaje }
    } catch (error) {
      const data = error.response?.data
      if (data?.requiereConfirmacion) {
        return rejectWithValue({
          mensaje: data.mensaje,
          cantidadBomberos: data.cantidadBomberos,
          requiereConfirmacion: true
        })
      }
      return rejectWithValue(data?.mensaje || 'Error al eliminar especialidad')
    }
  }
)

export const asignarBomberos = createAsyncThunk(
  'especialidades/asignarBomberos',
  async ({ especialidadId, bomberoIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/especialidades/${especialidadId}/bomberos`, { bomberoIds })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al asignar bomberos')
    }
  }
)

export const desasignarBombero = createAsyncThunk(
  'especialidades/desasignarBombero',
  async ({ especialidadId, bomberoId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/especialidades/${especialidadId}/bomberos/${bomberoId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al desasignar bombero')
    }
  }
)

export const fetchBomberosDisponibles = createAsyncThunk(
  'especialidades/fetchBomberosDisponibles',
  async (especialidadId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/especialidades/${especialidadId}/bomberos-disponibles`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.mensaje || 'Error al obtener bomberos disponibles')
    }
  }
)

const initialState = {
  especialidades: [],
  especialidadActual: null,
  bomberosDisponibles: [],
  stats: null,
  loading: false,
  statsLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  asignarLoading: false,
  error: null,
  createError: null,
  updateError: null,
  deleteError: null
}

const especialidadesSlice = createSlice({
  name: 'especialidades',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
      state.createError = null
      state.updateError = null
      state.deleteError = null
    },
    clearEspecialidadActual: (state) => {
      state.especialidadActual = null
      state.bomberosDisponibles = []
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchEspecialidades.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEspecialidades.fulfilled, (state, action) => {
        state.loading = false
        state.especialidades = action.payload
      })
      .addCase(fetchEspecialidades.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch by ID
      .addCase(fetchEspecialidadById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEspecialidadById.fulfilled, (state, action) => {
        state.loading = false
        state.especialidadActual = action.payload
      })
      .addCase(fetchEspecialidadById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch stats
      .addCase(fetchEspecialidadesStats.pending, (state) => {
        state.statsLoading = true
      })
      .addCase(fetchEspecialidadesStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.stats = action.payload
      })
      .addCase(fetchEspecialidadesStats.rejected, (state, action) => {
        state.statsLoading = false
      })
      
      // Create
      .addCase(createEspecialidad.pending, (state) => {
        state.createLoading = true
        state.createError = null
      })
      .addCase(createEspecialidad.fulfilled, (state, action) => {
        state.createLoading = false
        state.especialidades.push(action.payload)
        state.especialidades.sort((a, b) => a.nombre.localeCompare(b.nombre))
      })
      .addCase(createEspecialidad.rejected, (state, action) => {
        state.createLoading = false
        state.createError = action.payload
      })
      
      // Update
      .addCase(updateEspecialidad.pending, (state) => {
        state.updateLoading = true
        state.updateError = null
      })
      .addCase(updateEspecialidad.fulfilled, (state, action) => {
        state.updateLoading = false
        const index = state.especialidades.findIndex(e => e.id === action.payload.id)
        if (index !== -1) {
          state.especialidades[index] = action.payload
        }
        if (state.especialidadActual?.id === action.payload.id) {
          state.especialidadActual = action.payload
        }
      })
      .addCase(updateEspecialidad.rejected, (state, action) => {
        state.updateLoading = false
        state.updateError = action.payload
      })
      
      // Delete
      .addCase(deleteEspecialidad.pending, (state) => {
        state.deleteLoading = true
        state.deleteError = null
      })
      .addCase(deleteEspecialidad.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.especialidades = state.especialidades.filter(e => e.id !== action.payload.id)
      })
      .addCase(deleteEspecialidad.rejected, (state, action) => {
        state.deleteLoading = false
        state.deleteError = action.payload
      })
      
      // Asignar bomberos
      .addCase(asignarBomberos.pending, (state) => {
        state.asignarLoading = true
      })
      .addCase(asignarBomberos.fulfilled, (state, action) => {
        state.asignarLoading = false
        if (state.especialidadActual?.id === action.payload.id) {
          state.especialidadActual = {
            ...state.especialidadActual,
            bomberos: action.payload.bomberos,
            cantidadBomberos: action.payload.bomberos.length
          }
        }
        // Actualizar en la lista
        const index = state.especialidades.findIndex(e => e.id === action.payload.id)
        if (index !== -1) {
          state.especialidades[index] = {
            ...state.especialidades[index],
            bomberos: action.payload.bomberos,
            cantidadBomberos: action.payload.bomberos.length
          }
        }
      })
      .addCase(asignarBomberos.rejected, (state, action) => {
        state.asignarLoading = false
        state.error = action.payload
      })
      
      // Desasignar bombero
      .addCase(desasignarBombero.pending, (state) => {
        state.asignarLoading = true
      })
      .addCase(desasignarBombero.fulfilled, (state, action) => {
        state.asignarLoading = false
        if (state.especialidadActual?.id === action.payload.id) {
          state.especialidadActual = {
            ...state.especialidadActual,
            bomberos: action.payload.bomberos,
            cantidadBomberos: action.payload.bomberos.length
          }
        }
        // Actualizar en la lista
        const index = state.especialidades.findIndex(e => e.id === action.payload.id)
        if (index !== -1) {
          state.especialidades[index] = {
            ...state.especialidades[index],
            bomberos: action.payload.bomberos,
            cantidadBomberos: action.payload.bomberos.length
          }
        }
      })
      .addCase(desasignarBombero.rejected, (state, action) => {
        state.asignarLoading = false
        state.error = action.payload
      })
      
      // Fetch bomberos disponibles
      .addCase(fetchBomberosDisponibles.fulfilled, (state, action) => {
        state.bomberosDisponibles = action.payload
      })
  }
})

export const { clearError, clearEspecialidadActual } = especialidadesSlice.actions
export default especialidadesSlice.reducer
